/* eslint-disable @typescript-eslint/restrict-template-expressions */
// src/DesktopOnlyGate.tsx
import { useEffect, useMemo, useState } from "react"

type StorageMode = "local" | "session" | "none"

type Props = {
  /** Block below this width (in CSS px). Default: 1024 */
  minWidth?: number
  /** Message shown to blocked users */
  message?: string
  /** Treat tablets (coarse pointer) as allowed */
  allowCoarsePointer?: boolean
  /** Remember user's bypass choice; default: "local" */
  rememberWith?: StorageMode
  /** Storage key for the bypass flag */
  storageKey?: string
  children: React.ReactNode
}

function isClient(): boolean {
  return typeof window !== "undefined" && typeof navigator !== "undefined"
}

function mq(query: string): boolean {
  if (!isClient() || typeof window.matchMedia !== "function") return false
  return window.matchMedia(query).matches
}

type NavigatorUAData = { mobile?: boolean } | undefined
function hasUAData(n: Navigator): n is Navigator & { userAgentData: NavigatorUAData } {
  return "userAgentData" in n
}

function isProbablyMobileOrTablet(opts: { minWidth: number; allowCoarsePointer: boolean }): boolean {
  if (!isClient()) return false
  const narrow = mq(`(max-width: ${opts.minWidth}px)`)
  const portrait = mq("(orientation: portrait)")
  const coarse = mq("(pointer: coarse)")
  const touchPoints = navigator.maxTouchPoints > 0

  let uaMobile = false
  if (hasUAData(navigator)) {
    uaMobile = Boolean(navigator.userAgentData?.mobile)
  } else {
    const re = /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i
    uaMobile = re.test(navigator.userAgent)
  }

  const coarseBlock = opts.allowCoarsePointer ? false : coarse
  return narrow || portrait || coarseBlock || touchPoints || uaMobile
}

function getStorage(mode: StorageMode): Storage | null {
  if (!isClient()) return null
  try {
    if (mode === "local") return window.localStorage
    if (mode === "session") return window.sessionStorage
  } catch {
    // Storage might be disabled; fall through to null
  }
  return null
}

export default function DesktopOnlyGate({
  minWidth = 1024,
  message = "This app is not optimized for mobile. Please use a desktop/laptop for the best experience.",
  allowCoarsePointer = false,
  rememberWith = "local",
  storageKey = "desktop-only-gate:bypass",
  children,
}: Props) {
  // Check if user previously bypassed
  const initialBypassed = (() => {
    const s = getStorage(rememberWith)
    const v = s?.getItem(storageKey)
    return v === "1"
  })()

  const [bypassed, setBypassed] = useState<boolean>(initialBypassed)
  const [blocked, setBlocked] = useState<boolean>(
    () => !initialBypassed && isProbablyMobileOrTablet({ minWidth, allowCoarsePointer }),
  )

  // Media queries to subscribe to
  const queries = useMemo(
    () => [`(max-width: ${minWidth}px)`, "(orientation: portrait)", "(pointer: coarse)"],
    [minWidth],
  )

  useEffect(() => {
    if (!isClient() || typeof window.matchMedia !== "function") return

    const update = () => {
      if (bypassed) {
        setBlocked(false)
        return
      }
      setBlocked(isProbablyMobileOrTablet({ minWidth, allowCoarsePointer }))
    }

    const mqls = queries.map(q => window.matchMedia(q))
    mqls.forEach(mql => mql.addEventListener("change", update))
    window.addEventListener("resize", update)
    window.addEventListener("orientationchange", update)
    update()

    return () => {
      mqls.forEach(mql => mql.removeEventListener("change", update))
      window.removeEventListener("resize", update)
      window.removeEventListener("orientationchange", update)
    }
  }, [queries, minWidth, allowCoarsePointer, bypassed])

  // Lock/unlock scroll only when blocked
  useEffect(() => {
    if (!isClient()) return
    const el = document.documentElement
    const prev = el.style.overflow
    if (blocked) el.style.overflow = "hidden"
    return () => {
      el.style.overflow = prev
    }
  }, [blocked])

  const handleBypass = () => {
    const s = getStorage(rememberWith)
    try {
      s?.setItem(storageKey, "1")
    } catch {
      // ignore storage errors
    }
    setBypassed(true)
    setBlocked(false)
  }

  if (blocked) {
    return (
      <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Desktop only notice">
        <div style={cardStyle}>
          <h1 style={{ margin: 0, fontSize: "1.25rem" }}>Desktop recommended</h1>
          <p style={{ margin: "0.75rem 0 1rem" }}>{message}</p>
          <button type="button" onClick={handleBypass} style={buttonStyle} aria-label="Use the website anyway">
            Use nativefingers anyway
          </button>
          <p style={hintStyle}>Tip: rotate to landscape or enlarge your window to try again.</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  display: "grid",
  placeItems: "center",
  padding: "24px",
  background: "rgba(0,0,0,0.75)",
  color: "#fff",
  zIndex: 9999,
  textAlign: "center",
}

const cardStyle: React.CSSProperties = {
  maxWidth: 520,
  background: "rgba(0,0,0,0.4)",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 12,
  padding: "20px 24px",
  backdropFilter: "blur(6px)",
}

const buttonStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "10px 14px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.35)",
  background: "rgba(255,255,255,0.1)",
  color: "#fff",
  fontWeight: 600,
  cursor: "pointer",
}

const hintStyle: React.CSSProperties = {
  opacity: 0.8,
  fontSize: "0.85rem",
  marginTop: "0.75rem",
}
